<template>
    <main class="content">
        <header class="content-header">
            <section class="content-header-section">
                <router-link :to="`/${ $options.module }`" class="button button-back">
                    {{ $t('general.form.buttons.cancel') }}
                </router-link>
            </section>
            <section class="content-header-section">
                <button
                    type="button"
                    class="button button-gray"
                    @click="actionSave"
                >
                    {{ $t('general.form.buttons.save') }}
                </button>
                <button
                    type="button"
                    class="button button-blue"
                    @click="actionPublish"
                >
                    {{ $t('general.form.buttons.publish') }}
                </button>
            </section>
        </header>
        <div v-if="element" class="content-form">
            <input-i18n
                field="title"
                :label="$t(`pages.${ $options.module }.form.labels.title`)"
                :placeholder="$t(`pages.${ $options.module }.form.placeholders.title`)"
                :locales="element.locales"
                :errors="errors"
                :currentLanguage="currentLanguage"
                :maxlength="100"
                @actionChangeLang="actionChangeLang"
            />
            <div class="form-items">
                <draggable
                    v-model="element.items"
                    @start="drag = true"
                    @end="drag = false"
                    handle=".form-item-drag-handler"
                    ghost-class="ghost"
                >
                    <div
                        v-for="(item, itemIndex) in element.items"
                        :key="itemIndex"
                        class="form-item"
                    >
                        <div class="form-item-drag-handler"/>
                        <div class="form-item-content">
                            <div class="form-input-header">
                                <div class="label">{{ $t(`pages.${ $options.module }.form.labels.item`) }}</div>
                                <div class="languages">
                                    <span
                                        v-for="lang in $options.languages"
                                        :key="lang"
                                        :class="{ 'lang-button': true, active: currentLanguage === lang, error: errorHasAny(item.errorIndex, lang) }"
                                        @click="actionChangeLang(lang)"
                                    >
                                        {{ lang }}
                                    </span>
                                </div>
                            </div>
                            <div class="form-input-field">
                                <input
                                    v-model="item.locales[currentLanguage].title"
                                    type="text"
                                    v-bind:placeholder="$t(`pages.${ $options.module }.form.placeholders.item_text`)"
                                />
                                <div v-if="errorHas(item.errorIndex, currentLanguage, 'title')" class="form-input-error">
                                    {{ errorMessage(item.errorIndex, currentLanguage, 'title') }}
                                </div>
                            </div>
                            <div class="form-input-field">
                                <input
                                    v-model="item.locales[currentLanguage].description"
                                    type="text"
                                    v-bind:placeholder="$t(`pages.${ $options.module }.form.placeholders.item_description`)"
                                />
                                <div v-if="errorHas(item.errorIndex, currentLanguage, 'description')" class="form-input-error">
                                    {{ errorMessage(item.errorIndex, currentLanguage, 'description') }}
                                </div>
                            </div>
                            <div v-if="item.attachments && item.attachments.length > 0" class="form-attachments">
                                <div class="form-attachments-title">{{ $t(`pages.${ $options.module }.form.labels.attached_files`) }}</div>
                                <div
                                    v-for="(attachment, attachmentIndex) in item.attachments"
                                    :key="attachmentIndex"
                                    class="form-attachments-item"
                                >
                                    <span class="attachment-icon"></span>
                                    <span v-if="!attachment.id" class="attachment-file">{{ attachment.title }}</span>
                                    <span v-if="attachment.id > 0" class="attachment-file">
                                        <a
                                            :href="getAttachmentUrl(attachment.id)"
                                            target="_blank"
                                        >
                                            {{ attachment.title }}
                                        </a>
                                    </span>
                                    <span v-on:click="attachmentRemove(itemIndex, attachmentIndex)" class="attachment-cancel"></span>
                                </div>
                            </div>

                            <div class="form-attachments-add">
                                <span class="icon"></span>
                                <span class="name">{{ $t(`pages.${ $options.module }.form.labels.add_attachment`) }}</span>
                                <input type="file" class="file-input" @change="attachmentAdd($event, itemIndex)"/>
                            </div>
                        </div>
                        <div class="form-item-actions">
                            <span
                                v-if="element.items.length > 1"
                                v-on:click="itemRemove(itemIndex)"
                                class="action action-remove"
                            />
                            <span
                                v-on:click="itemAdd(itemIndex)"
                                class="action action-add"
                            />
                        </div>
                    </div>
                </draggable>
            </div>
        </div>
        <modal
            v-if="remove.item.open"
            :title="$t(`pages.${ $options.module }.form.remove.item_title`)"
            :message="$t(`pages.${ $options.module }.form.remove.item_message`)"
            :cancelShow="true"
            :confirmLabel="$t(`pages.${ $options.module }.form.remove.button`)"
            @cancel="itemRemoveCancel"
            @confirm="itemRemoveConfirm"
        />
        <modal
            v-if="remove.attachment.open"
            :title="$t(`pages.${ $options.module }.form.remove.attachment_title`)"
            :message="$t(`pages.${ $options.module }.form.remove.attachment_message`)"
            :cancelShow="true"
            :confirmLabel="$t(`pages.${ $options.module }.form.remove.button`)"
            @cancel="attachmentRemoveCancel"
            @confirm="attachmentRemoveConfirm"
        />
    </main>
</template>

<script>
import draggable from 'vuedraggable';

import api from '@/api';

import PopDownMixin from '@/components/PopDownMixin';
import FormMixin from '@/components/form/FormMixin';

import Modal from '@/components/Modal.vue';

const emptyElement = {
    locales: {
        de: {
            title: '',
        },
        it: {
            title: '',
        },
        fr: {
            title: '',
        },
        en: {
            title: '',
        },
    },
    items: [],
};

const emptyItem = {
    locales: {
        de: {
            title: '',
            description: '',
        },
        fr: {
            title: '',
            description: '',
        },
        it: {
            title: '',
            description: '',
        },
        en: {
            title: '',
            description: '',
        },
    },
    attachments: [],
};

export default {
    mixins: [FormMixin, PopDownMixin],
    name: 'ChecklistsForm',
    module: 'checklists',
    components: {
        draggable,
        Modal,
    },
    emptyChild: emptyItem,
    emptyElement,
    data() {
        return {
            remove: {
                item: {
                    open: false,
                    index: null,
                },
                attachment: {
                    open: false,
                    itemIndex: null,
                    attachmentIndex: null,
                },
            },
            files: {},
            filesIndex: 1,
        };
    },
    methods: {
        getAttachmentUrl(id) {
            return api[this.$options.module].attachmentUrl(id);
        },
        errorField(index, lang, field) {
            return `items.${ index }.locales.${ lang }.${ field }`;
        },
        errorHas(index, lang, field) {
            return this.errors && typeof this.errors[this.errorField(index, lang, field)] !== 'undefined' && this.errors[this.errorField(index, lang, field)];
        },
        errorHasAny(index, lang) {
            return this.errorHas(index, lang, 'title') || this.errorHas(index, lang, 'description');
        },
        errorMessage(index, lang, field) {
            return this.errors[this.errorField(index, lang, field)].join('<br/>');
        },
        itemAdd(prevIndex) {
            this.element.items.splice(prevIndex + 1, 0, {
                ...JSON.parse(JSON.stringify(emptyItem)),
            });
        },
        itemRemove(index) {
            this.remove.item.open = true;
            this.remove.item.index = index;
        },
        itemRemoveCancel() {
            this.remove.item.open = false;
            this.remove.item.index = null;
        },
        itemRemoveConfirm() {
            this.element.items.splice(this.remove.item.index, 1);
            this.remove.item.open = false;
            this.remove.item.index = null;
        },
        attachmentAdd(e, itemIndex) {
            const file = e.target.files[0];
            this.element.items[itemIndex].attachments.push({
                id: 0,
                title: file.name,
                filename: `attachment_${ this.filesIndex }`,
            });
            this.files[`attachment_${ this.filesIndex }`] = file;
            this.filesIndex += 1;
        },
        attachmentRemove(itemIndex, attachmentIndex) {
            this.remove.attachment.open = true;
            this.remove.attachment.itemIndex = itemIndex;
            this.remove.attachment.attachmentIndex = attachmentIndex;
        },
        attachmentRemoveCancel() {
            this.remove.attachment.open = false;
            this.remove.attachment.itemIndex = null;
            this.remove.attachment.attachmentIndex = null;
        },
        attachmentRemoveConfirm() {
            const { itemIndex, attachmentIndex } = this.remove.attachment;

            this.element.items[itemIndex].attachments.splice(attachmentIndex, 1);
            this.remove.attachment.open = false;
            this.remove.attachment.itemIndex = null;
            this.remove.attachment.attachmentIndex = null;
        },
        async actionSave() {
            if (this.actionValidate()) {
                const action = (this.type === 'edit') ? 'update' : 'create';
                try {
                    await this.$store.dispatch(`${ this.$options.module }/${ action }`, {
                        element: this.element,
                        files: this.files,
                    });
                    if (this.error === null) {
                        this.actionBackToList(`success_${ action }`);
                    } else {
                        for (let i = 0; i < this.element.items.length; i += 1) {
                            this.element.items[i].errorIndex = i;
                        }
                        this.popOpen(this.$t(`general.errors.form.${ this.error }`), 'error');
                    }
                } catch (err) {
                    if (process.env.NODE_ENV !== 'production') {
                        console.log(err);
                    }
                    this.actionBackToList('error_server');
                }
            }
        },
    },
};
</script>
